/**
 * Shows what went wrong instead of a blank page (1.3.0 Help fix). Without a boundary, an error while rendering (or a
 * lazily loaded page whose code failed to load) unmounts the whole app and leaves only the page background.
 * `resetKey` (e.g. the address) clears the error when it changes, so another page can be opened. A module that failed
 * to load reloads the page once by itself (lib/chunkReload.ts), since the browser will not fetch it again otherwise.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { claimChunkReload, isChunkLoadError } from '../lib/chunkReload';

interface Props { resetKey?: unknown; testId?: string; title?: string; children: ReactNode }
interface State { error: Error | null; reloading: boolean }

const session = (): Storage | null => { try { return window.sessionStorage; } catch { return null; } };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('PRISM: a page failed to render', error, info.componentStack);
    if (isChunkLoadError(error) && claimChunkReload(session(), window.location.href)) {
      this.setState({ reloading: true });
      window.location.reload();
    }
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null, reloading: false });
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    const chunk = isChunkLoadError(error);
    return (
      <div className="empty" role="alert" data-testid={this.props.testId ?? 'page-error'}>
        <div className="empty-inner">
          <div className="empty-icon"><AlertTriangle size={24} /></div>
          <h2>{this.props.title ?? 'This page could not be shown'}</h2>
          <p>{chunk ? 'Part of PRISM did not load. Reload the page to try again.' : 'Something went wrong while showing it. Reload the page, or open another one.'}</p>
          <p className="faint" style={{ fontSize: 12, fontFamily: 'var(--mono)', wordBreak: 'break-word' }} data-testid="page-error-message">{error.message || String(error)}</p>
          <button type="button" className="btn" disabled={reloading} onClick={() => window.location.reload()}>{reloading ? 'Reloading…' : 'Reload'}</button>
        </div>
      </div>
    );
  }
}
